import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, where, onSnapshot, updateDoc, arrayUnion, writeBatch, orderBy, limit, deleteField } from 'firebase/firestore';
import { auth, db } from '../firebase';
import Profile from './Profile';
import FindMatches from './FindMatches';
import Messages from './Messages';
import Sessions from './Sessions';
import Resources from './Resources';
import StudyMaterials from './StudyMaterials';
import Settings from './Settings';
import Today from './Today';
import Onboarding from './Onboarding';
import { profileReady, loadMatches, loadContacts } from '../matchList';
import useOutsideClose from '../useOutsideClose';
import { Icon, Avatar, LoadingScreen } from './ui';
import { hideSplash, onAppLink, tapFeedback } from '../native';
import { toast, confirmSheet } from './feedback';
import { pushPermission, enablePush, startPush, stopPush } from '../push';
import { VideoCallHost, startCall } from './VideoCall';
import Progress from './Progress';
import { useBlocked, withoutBlocked } from '../blocking';
import { sessionStart } from '../dateUtils';
import { useBackClose, watchSheets } from '../backStack';

// Five tabs (design decision D2, Nov 2). Shared Docs and Resources open from Profile.
const TABS = [
  ['today', 'Today', 'home'],
  ['matches', 'Matches', 'matches'],
  ['messages', 'Messages', 'messages'],
  ['sessions', 'Sessions', 'calendar'],
  ['profile', 'Profile', 'profile'],
];
// Screens reached from Profile keep Profile as the active tab, with a back arrow to it.
const UNDER_PROFILE = ['settings', 'materials', 'resources'];
const PUSHED = ['materials', 'resources', 'progress'];
// The desktop top nav has room for Shared docs and Resources too (brief 025);
// Profile and Settings open from the avatar menu there. Phones keep the five tabs.
const DESKTOP_TABS = [
  ['today', 'Today'],
  ['matches', 'Matches'],
  ['messages', 'Messages'],
  ['sessions', 'Sessions'],
  ['materials', 'Shared docs'],
  ['resources', 'Resources'],
];

const TITLES = { today:'Today', matches:'Matches', messages:'Messages', sessions:'Sessions', materials:'Shared Docs', profile:'Profile', settings:'Settings', resources:'Resources', progress:'Progress' };

function Dashboard({ user }) {
  // Set once the profile has loaded: Today, or Profile while it's incomplete.
  const [currentView, setCurrentView] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadMsgs, setUnreadMsgs] = useState([]);
  const [pendingReqs, setPendingReqs] = useState([]);
  const [allSharedDocs, setSharedDocs] = useState([]);
  const [allNotifs, setInboxNotifs] = useState([]);
  // Badges and the bell ignore people I've blocked.
  const blocked = useBlocked(user.uid);
  const visibleUnread = useMemo(() => withoutBlocked(unreadMsgs, blocked, m => m.fromUid), [unreadMsgs, blocked]);
  const unreadCount = visibleUnread.length;
  const visiblePending = useMemo(() => withoutBlocked(pendingReqs, blocked, s => s.fromUid), [pendingReqs, blocked]);
  const pendingCount = visiblePending.length;
  const sharedDocs = useMemo(() => withoutBlocked(allSharedDocs, blocked, d => d.from), [allSharedDocs, blocked]);
  const inboxNotifs = useMemo(() => withoutBlocked(allNotifs, blocked, n => n.fromUid), [allNotifs, blocked]);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  // Matches live here, not in FindMatches, so they survive tab switches (review F1).
  // list is null until the first load; Today and Matches both read it.
  const [matchList, setMatchList] = useState(null);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [contacts, setContacts] = useState(() => new Set());
  const matchesAt = useRef(0);
  const profileRef = useRef(null);
  profileRef.current = userProfile;
  const closeInbox = useCallback(() => setInboxOpen(false), []);
  const closeAccount = useCallback(() => setAccountOpen(false), []);
  useOutsideClose('.bell-notifs', inboxOpen, closeInbox);
  useOutsideClose('.account', accountOpen, closeAccount);
  const [chatOpen, setChatOpen] = useState(false);
  // Which chat a tapped notification should open inside Messages.
  const [msgIntent, setMsgIntent] = useState(null);
  const [actionsEl, setActionsEl] = useState(null);
  const contactCleaned = useRef(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'users', user.uid), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setUserProfile(data);
        setCurrentView(v => v || (profileReady(data) ? 'today' : 'onboarding'));
        // Email/phone used to live on the public profile; remove them once.
        if (!contactCleaned.current && ('email' in data || 'phone' in data)) {
          contactCleaned.current = true;
          updateDoc(doc(db, 'users', user.uid), { email: deleteField(), phone: deleteField() })
            .catch(e => console.warn('contact cleanup failed', e));
        }
      } else setCurrentView(v => v || 'onboarding');
      setLoading(false);
    }, e => { console.error(e); setCurrentView(v => v || 'profile'); setLoading(false); });
    return unsub;
  }, [user.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db,'messages'), where('toUid','==',user.uid));
    return onSnapshot(q, snap => setUnreadMsgs(snap.docs.map(d => d.data()).filter(m => !m.read)));
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db,'sessions'), where('toUid','==',user.uid));
    // Requests still waiting for an answer whose time hasn't passed (as in Sessions).
    return onSnapshot(q, snap => setPendingReqs(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => {
      const t = sessionStart(s)?.getTime();
      return s.status === 'pending' && (t == null || t + 2 * 60 * 60 * 1000 > Date.now());
    })));
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db,'materials'), where('sharedWith','array-contains',user.uid));
    return onSnapshot(q, snap => setSharedDocs(snap.docs.map(d => ({ id: d.id, from: d.data().uploadedByUid, readBy: d.data().readBy || [] }))));
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db,'notifications'), where('toUid','==',user.uid), orderBy('createdAt','desc'), limit(30));
    return onSnapshot(q, snap => setInboxNotifs(snap.docs.map(d=>({id:d.id,...d.data()}))), e => console.error(e));
  }, [user?.uid]);

  const unseenDocs = sharedDocs.filter(d => !d.readBy.includes(user.uid)).length;

  useEffect(() => {
    if (currentView !== 'materials') return;
    sharedDocs.forEach(d => {
      if (!d.readBy.includes(user.uid)) {
        updateDoc(doc(db,'materials',d.id), { readBy: arrayUnion(user.uid) }).catch(()=>{});
      }
    });
  }, [currentView, sharedDocs, user.uid]);

  const relTime = ts => {
    if (!ts?.toDate) return '';
    const diff = Date.now() - ts.toDate().getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const inboxUnread = inboxNotifs.filter(n => !n.read).length;

  const markAllRead = useCallback(async () => {
    const batch = writeBatch(db);
    inboxNotifs.filter(n => !n.read).forEach(n => batch.update(doc(db,'notifications',n.id),{read:true}));
    await batch.commit().catch(()=>{});
  }, [inboxNotifs]);

  const markRead = useCallback(async (n) => {
    if (!n.read) updateDoc(doc(db,'notifications',n.id),{read:true}).catch(()=>{});
    setInboxOpen(false);
    if ((n.type === 'message' || n.type === 'invite_joined') && n.fromUid) {
      setMsgIntent({ convo: { uid: n.fromUid, name: n.fromName }, key: Date.now() });
      setCurrentView('messages');
    } else if (n.type === 'session_request' || n.type === 'session_accepted' || n.type === 'session_declined') {
      setCurrentView('sessions');
    } else if (n.type === 'document_shared') {
      setCurrentView('materials');
    }
  }, []);

  // Loads matches (and who I've been in touch with) in the background. Opening
  // Matches or Today refreshes them when they're over a minute old; force skips that.
  const refreshMatches = useCallback(async (force = false) => {
    const me = profileRef.current;
    if (!profileReady(me)) { setMatchList([]); return; }
    if (!force && Date.now() - matchesAt.current < 60000) return;
    matchesAt.current = Date.now();
    setMatchesLoading(true);
    try {
      const [list, people] = await Promise.all([loadMatches(user.uid, me), loadContacts(user.uid)]);
      setMatchList(list);
      setContacts(people);
    } catch (e) {
      console.error(e);
      matchesAt.current = 0;
      setMatchList(l => l || []);
      toast('Could not load your matches. Check your connection and try again.', { type: 'error' });
    } finally { setMatchesLoading(false); }
  }, [user.uid]);

  useEffect(() => {
    if (currentView === 'matches' || currentView === 'today') refreshMatches();
  }, [currentView, refreshMatches]);

  // Phones (design I4): the compact title shows once the page title has scrolled away.
  const [titleGone, setTitleGone] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const t = document.querySelector('.app-main .page-title');
      setTitleGone(!!t && t.getBoundingClientRect().bottom < 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [currentView]);

  // Where Back from a pushed screen returns to: the screen it was opened from.
  const backTo = useRef('today');
  const go = view => {
    if (view !== currentView && !PUSHED.includes(currentView)) backTo.current = currentView;
    setMsgIntent(null); setAccountOpen(false); setCurrentView(view);
  };
  // Every screen starts at the top (review R2).
  useEffect(() => { window.scrollTo(0, 0); }, [currentView]);

  // Back (or the iPhone's edge swipe) closes Settings and any open sheet.
  useBackClose(currentView === 'settings', () => go('profile'));
  // Shared docs, Resources and Progress are pushed screens too (review B1): Back (and
  // the iPhone edge swipe) returns to where they were opened from.
  useBackClose(PUSHED.includes(currentView), () => go(backTo.current));
  useEffect(() => watchSheets(), []);

  // iPhone app: the launch splash gives way once the first screen can show.
  const ready = !loading && !!currentView;
  useEffect(() => { if (ready) hideSplash(); }, [ready]);

  // iPhone app: keep this phone's push token current and open the right screen when
  // a push is tapped. The first time, explain push in a sheet before iOS asks.
  useEffect(() => {
    if (loading) return;
    let stop = () => {};
    let cancelled = false;
    const open = d => markRead({ id: d.notificationId, read: !d.notificationId, type: d.type, fromUid: d.fromUid, fromName: d.fromName });
    (async () => {
      try {
        stop = await startPush(user.uid, open);
        if (cancelled) { stop(); return; }
        if ((await pushPermission()) !== 'prompt') return;
        // Ask once: remembered only after an answer, so closing the app mid-sheet asks again.
        let asked = false;
        try { asked = localStorage.getItem('mxm.pushAsked') === '1'; } catch {}
        if (asked) return;
        const yes = await confirmSheet({
          title: 'Turn on notifications?',
          message: 'Get a notification on this iPhone when a study partner messages you, asks for a session, answers your request or shares a document. You can change this any time in Settings.',
          confirmLabel: 'Turn on',
          cancelLabel: 'Not now',
        });
        try { localStorage.setItem('mxm.pushAsked', '1'); } catch {}
        if (yes) await enablePush(user.uid);
      } catch (e) { console.warn('push setup failed', e); }
    })();
    return () => { cancelled = true; stop(); };
  }, [loading, user.uid, markRead]);

  // A calendar event's link (medxmeet.com/?join=<session id>) opens that session's
  // video call. Runs once the dashboard is ready; signing in first keeps the link.
  // In the iPhone app the same link arrives as a universal link (onAppLink).
  useEffect(() => {
    if (loading) return;
    const join = sessionId => {
      setMsgIntent(null);
      setCurrentView('sessions');
      getDoc(doc(db, 'sessions', sessionId))
        .then(snap => {
          const s = snap.data();
          const mine = s.fromUid === user.uid;
          startCall(mine ? s.toUid : s.fromUid, (mine ? s.toName : s.fromName) || 'your study partner');
        })
        .catch(() => toast('This video link belongs to another account. Sign in as that account, or join from Sessions.', { type: 'error' }));
    };
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('join');
    if (sessionId) {
      params.delete('join');
      const rest = params.toString();
      window.history.replaceState(window.history.state, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
      join(sessionId);
    }
    return onAppLink(url => { const id = url.searchParams.get('join'); if (id) join(id); });
  }, [loading, user.uid]);

  // Escape closes whichever sheet or modal is open (each has a close button).
  useEffect(() => {
    const onKey = e => {
      if (e.key !== 'Escape') return;
      const close = document.querySelectorAll('.modal-close');
      if (close.length) close[close.length - 1].click();
      else { setInboxOpen(false); setAccountOpen(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);


  const handleLogout = async () => { try { await stopPush(user.uid); await signOut(auth); } catch(e) { console.error(e); } };

  const showNotification = (title, message) => toast(message, { title });

  const openChat = (uid, name) => { setMsgIntent({ convo: { uid, name }, key: Date.now() }); setAccountOpen(false); setCurrentView('messages'); };

  if (loading || !currentView) return <LoadingScreen />;

  // First-run setup (brief 020) takes the whole screen; it ends on Matches with results loading.
  if (currentView === 'onboarding') return (
    <Onboarding user={user} userProfile={userProfile} onLogout={handleLogout} onDone={() => {
      matchesAt.current = 0;
      setMatchList(null);
      setCurrentView('matches');
    }} />
  );

  // Progress (brief 026) opens from Today, so Today stays the active tab and the back arrow returns there.
  const activeTab = UNDER_PROFILE.includes(currentView) ? 'profile' : currentView === 'progress' ? 'today' : currentView;
  // Requests badge Sessions (brief 019); new shared documents are counted on Today now that Docs has no tab.
  const tabBadge = view => view === 'messages' ? unreadCount : view === 'sessions' ? pendingCount : view === 'today' ? unseenDocs : 0;
  const tabName = (view, label, badge = tabBadge) => { const n = badge(view); return n > 0 ? `${label}, ${n} ${view === 'today' || view === 'materials' ? 'new' : view === 'sessions' ? (n > 1 ? 'requests' : 'request') : 'unread'}` : label; };
  // On desktop the new-documents count sits on Shared docs itself.
  const deskBadge = view => view === 'materials' ? unseenDocs : view === 'today' ? 0 : tabBadge(view);

  const bell = (
    <div className="bell bell-notifs">
      <button className="btn-icon small plain" onClick={() => setInboxOpen(o => !o)} aria-label={inboxUnread > 0 ? `Notifications, ${inboxUnread} unread` : 'Notifications'} aria-expanded={inboxOpen}>
        <Icon name="bell" />
        {inboxUnread > 0 && <span className="badge bell-badge">{inboxUnread > 99 ? '99+' : inboxUnread}</span>}
      </button>
      {inboxOpen && (
        <div className="bell-menu" role="dialog" aria-label="Notifications">
          <div className="bell-menu-head">
            <strong>Notifications</strong>
            {inboxUnread > 0 && <button className="btn-text" onClick={markAllRead}>Mark all read</button>}
            <button className="btn-icon small plain bell-sheet-close" onClick={closeInbox} aria-label="Close notifications"><Icon name="close" size={20} /></button>
          </div>
          {inboxNotifs.length === 0
            ? <div className="bell-empty">No notifications yet.</div>
            : inboxNotifs.map(n => {
              // Rows (design F25): avatar, the sender's name in bold, muted time, a dot while unread.
              const name = n.fromName || '';
              const rest = name && n.text?.startsWith(name) ? n.text.slice(name.length) : null;
              return (
                <button key={n.id} className={`bell-item ${n.read ? '' : 'unread'}`} onClick={() => markRead(n)}>
                  <Avatar name={name || '?'} size={40} />
                  <span className="bell-item-main">
                    <span className="bell-item-text">{rest !== null ? <><strong>{name}</strong>{rest}</> : n.text}</span>
                    <span className="bell-item-time">{relTime(n.createdAt)}</span>
                  </span>
                  {!n.read && <span className="bell-dot" aria-label="Unread" />}
                </button>
              );
            })
          }
        </div>
      )}
    </div>
  );

  return (
    <div className={`app-shell ${currentView === 'messages' && chatOpen ? 'chat-open' : ''}`}>
      <header className="topnav">
        <div className="topnav-inner">
          <button className="topnav-home" onClick={() => go('today')} aria-label="MedXMeet, go to Today" aria-current={currentView === 'today' ? 'page' : undefined}>
            <img src="/logo.svg" alt="" className="topnav-logo" />
          </button>
          <nav className="topnav-tabs" aria-label="Main">
            {DESKTOP_TABS.map(([view, label]) => (
              <button key={view} className={`topnav-tab ${currentView === view ? 'active' : ''}`} onClick={() => go(view)} aria-current={currentView === view ? 'page' : undefined} aria-label={tabName(view, label, deskBadge)}>
                {label}
                {deskBadge(view) > 0 && <span className="badge">{deskBadge(view)}</span>}
              </button>
            ))}
          </nav>
          <div className="topnav-right">
            {bell}
            <div className="bell account">
              <button className="topnav-avatar" onClick={() => setAccountOpen(o => !o)} aria-label="Account menu" aria-haspopup="menu" aria-expanded={accountOpen}>
                <Avatar src={userProfile?.profilePicture} name={userProfile?.name} size={36} />
              </button>
              {accountOpen && (
                <div className="bell-menu account-menu" role="menu">
                  <div className="account-menu-head">
                    <strong>{userProfile?.name || 'Your account'}</strong>
                    <span>{user.email}</span>
                  </div>
                  <button role="menuitem" className="account-item" onClick={() => go('profile')}><Icon name="profile" />Profile</button>
                  <button role="menuitem" className="account-item" onClick={() => go('settings')}><Icon name="gear" />Settings</button>
                  <button role="menuitem" className="account-item" onClick={() => { setAccountOpen(false); handleLogout(); }}><Icon name="logout" />Log out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className={`compact-bar mobile-only ${titleGone ? 'show' : ''}`} aria-hidden="true"><span>{TITLES[currentView]}</span></div>
      <main className="app-main">
        <div className="page-head">
          {activeTab !== currentView && (
            <button className={`btn-icon small plain page-back ${['settings', 'progress'].includes(currentView) ? '' : 'mobile-only'}`} onClick={() => go(activeTab)} aria-label={`Back to ${TITLES[activeTab]}`}>
              <Icon name="back" />
            </button>
          )}
          <h1 className="page-title">{TITLES[currentView]}</h1>
          <div className="page-head-actions">
            {currentView === 'profile' && (
              <button className="btn-icon small plain" onClick={() => go('settings')} aria-label="Settings">
                <Icon name="gear" />
              </button>
            )}
            <div className="page-actions-slot" ref={setActionsEl} />
            <div className="mobile-only">{bell}</div>
          </div>
        </div>

        {currentView === 'today' && <Today user={user} userProfile={userProfile} unreadMsgs={visibleUnread} pendingReqs={visiblePending} unseenDocs={unseenDocs} blocked={blocked} matches={matchList} go={go} openChat={openChat} />}
        {currentView === 'profile' && <Profile user={user} userProfile={userProfile} onProfileUpdate={setUserProfile} onFindMatches={() => go('matches')} onOpen={go} unseenDocs={unseenDocs} showNotification={showNotification} />}
        {currentView === 'matches' && <FindMatches user={user} userProfile={userProfile} showNotification={showNotification} actionsEl={actionsEl} matches={matchList} loading={matchesLoading} onRefresh={() => refreshMatches(true)} contacts={contacts} onFindMatches={() => go('profile')} onSetup={() => go('onboarding')} />}
        {currentView === 'messages' && <Messages key={msgIntent?.key || 'messages'} user={user} userProfile={userProfile} showNotification={showNotification} initialConvo={msgIntent?.convo} onChatChange={setChatOpen} onOpenDocs={() => go('materials')} onFindMatches={() => go('matches')} />}
        {currentView === 'sessions' && <Sessions user={user} userProfile={userProfile} showNotification={showNotification} onFindMatches={() => go('matches')} />}
        {currentView === 'progress' && <Progress user={user} userProfile={userProfile} go={go} />}
        {currentView === 'resources' && <Resources userProfile={userProfile} onGoToProfile={() => go('profile')} />}
        {currentView === 'materials' && <StudyMaterials user={user} userProfile={userProfile} showNotification={showNotification} onFindMatches={() => go('matches')} />}
        {currentView === 'settings' && <Settings user={user} userProfile={userProfile} onProfileUpdate={setUserProfile} onLogout={handleLogout} showNotification={showNotification} />}
      </main>

      <VideoCallHost />

      <nav className="tabbar" aria-label="Main">
        {TABS.map(([view, label, icon]) => (
          <button key={view} className={`tabbar-tab ${activeTab === view ? 'active' : ''}`} onClick={() => { if (view !== activeTab) tapFeedback(); go(view); }} aria-current={activeTab === view ? 'page' : undefined} aria-label={tabName(view, label)}>
            <span className="tabbar-icon">
              <Icon name={icon} />
              {tabBadge(view) > 0 && <span className="badge tabbar-badge">{tabBadge(view)}</span>}
            </span>
            <span className="tabbar-label">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
export default Dashboard;